import React, { useState, useEffect } from 'react'
import { Button } from '../../../components/simple/button'
import { Survey } from 'survey-react-ui'
import { Model } from 'survey-core'
import { SurveyCreator } from 'survey-creator-react'
import 'survey-core/survey.i18n'
import { Loader } from '../../../components/simple/loader'
import * as Dialog from '@radix-ui/react-dialog'
import { IconTimes } from '../../../icons/library'
import { prebuiltTemplates } from './prebuiltTemplates'
import { makeRequest } from '../../../request'
import customTheme from './survey_theme.json'
import '../survey.css'

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'lg',
}: {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
}) => {
  const sizeClasses = {
    sm: 'max-w-lg',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
  }

  if (!isOpen) return null

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[1000] flex items-start justify-center overflow-auto bg-slate-500/25">
          <Dialog.Content
            className={`relative m-2 mt-16 flex flex-col gap-2 rounded-2xl bg-white p-4 shadow-lg focus:outline-none ${sizeClasses[size]}`}
            onInteractOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={() => onClose()}
          >
            <Dialog.Title className="mr-4 text-xl font-bold">{title}</Dialog.Title>
            <Dialog.Close
              className="absolute right-2 top-2 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
              onClick={onClose}
            >
              <IconTimes size="sm" />
            </Dialog.Close>
            <div className="mt-2">{children}</div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

interface TemplateSelectorProps {
  creator: SurveyCreator | null
  onTemplateSelected?: (templateJson: string) => void
  createMode?: boolean
}

type Template = {
  id: string
  name: string
  description: string
  json: string
}

type PreviousSurvey = {
  id: number
  name: string
  json: string
  created_at: string
}

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  creator,
  onTemplateSelected,
  createMode = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [templates, setTemplates] = useState<Template[]>([])
  const [previousSurveys, setPreviousSurveys] = useState<PreviousSurvey[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null)
  const [previewMode, setPreviewMode] = useState(false)
  const [confirmationMode, setConfirmationMode] = useState(false)
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'prebuilt' | 'previous'>('prebuilt')

  useEffect(() => {
    if (isModalOpen) {
      if (activeTab === 'previous') {
        loadPreviousSurveys()
      } else {
        setTemplates(prebuiltTemplates)
      }
    }
  }, [isModalOpen, activeTab])

  const loadPreviousSurveys = async () => {
    setLoading(true)
    try {
      const response = await makeRequest<{ success: boolean; surveys: PreviousSurvey[] }>('/form/getPreviousSurveys')
      if (response.success) {
        setPreviousSurveys(response.surveys)
      }
    } catch (error) {
      console.error('Error loading previous forms:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenModal = () => {
    setIsModalOpen(true)
    setPreviewMode(false)
    setConfirmationMode(false)
    setSelectedTemplate(null)
    setActiveTab('prebuilt')
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setPreviewMode(false)
    setConfirmationMode(false)
    setSelectedTemplate(null)
  }
  const handleSelectTemplate = async (template: Template) => {
    setLoading(true)
    let jsonToUse = template.json
    try {
      const response = await makeRequest<{ success: boolean; translatedJson: string }>(
        '/form/sendSurveyForTranslation',
        { surveyJson: template.json }
      )

      if (response.success && response.translatedJson) {
        jsonToUse = response.translatedJson
      }
    } catch (error) {
      console.error('Error translating template:', error)
    } finally {
      setLoading(false)
    }

    setSelectedTemplate({
      ...template,
      json: jsonToUse,
    })

    setPreviewMode(true)
  }

  const handleSelectPreviousSurvey = (survey: PreviousSurvey) => {
    setSelectedTemplate({
      id: String(survey.id),
      name: survey.name,
      description: `Created on ${new Date(survey.created_at).toLocaleDateString()}`,
      json: survey.json,
    })
    setPreviewMode(true)
  }
  const handleConfirmTemplate = () => {
    setPreviewMode(false)
    setConfirmationMode(true)
  }
  const handleApplyTemplate = () => {
    if (selectedTemplate) {
      if (creator) {
        creator.text = selectedTemplate.json
      }

      if (onTemplateSelected) {
        onTemplateSelected(selectedTemplate.json)
      }

      handleCloseModal()
    }
  }

  const handleBackToTemplates = () => {
    setPreviewMode(false)
    setConfirmationMode(false)
    setSelectedTemplate(null)
  }

  const renderTemplateList = () => {
    if (loading) {
      return <Loader />
    }

    return (
      <div>
        <div className="mb-4 flex border-b">
          <button
            className={`mr-4 border-b-2 px-4 py-2 ${activeTab === 'prebuilt' ? 'border-blue-500 text-blue-600' : 'border-transparent'}`}
            onClick={() => setActiveTab('prebuilt')}
          >
            Prebuilt Templates
          </button>
          <button
            className={`border-b-2 px-4 py-2 ${activeTab === 'previous' ? 'border-blue-500 text-blue-600' : 'border-transparent'}`}
            onClick={() => setActiveTab('previous')}
          >
            Your Previous Forms
          </button>
        </div>

        {activeTab === 'prebuilt' ? (
          templates.length === 0 ? (
            <div className="p-4 text-center text-gray-500">No templates available</div>
          ) : (
            <div className="md:grid-cols-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {templates.map((template, index) => (
                <div
                  key={template.id || index}
                  className="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition hover:shadow-md"
                  onClick={() => handleSelectTemplate(template)}
                >
                  <h3 className="mb-2 text-lg font-medium text-gray-900">{template.name}</h3>
                  <p className="text-sm text-gray-600">{template.description}</p>
                </div>
              ))}
            </div>
          )
        ) : previousSurveys.length === 0 ? (
          <div className="p-4 text-center text-gray-500">No previous forms available</div>
        ) : (
          <div className="md:grid-cols-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {previousSurveys.map((survey) => (
              <div
                key={survey.id}
                className="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition hover:shadow-md"
                onClick={() => handleSelectPreviousSurvey(survey)}
              >
                <h3 className="mb-2 text-lg font-medium text-gray-900">{survey.name}</h3>
                <p className="text-sm text-gray-600">Created on {new Date(survey.created_at).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  const renderPreview = () => {
    if (!selectedTemplate) return null

    try {
      const surveyModel = new Model(selectedTemplate.json)
      surveyModel.applyTheme(customTheme as any)
      return (
        <div className="w-full max-w-full overflow-x-auto">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="pr-4 text-lg font-medium text-gray-900">{selectedTemplate.name} Preview</h3>
            <Button onClick={handleConfirmTemplate} logo>
              Use This Template
            </Button>
          </div>
          <div className="min-w-[600px] rounded-lg border border-gray-200 bg-white p-4">
            <Survey model={surveyModel} />
          </div>
          <div className="mt-4 flex justify-start">
            <Button onClick={handleBackToTemplates}>Back to Templates</Button>
          </div>
        </div>
      )
    } catch (error) {
      console.error('Error previewing template:', error)
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          Error loading template preview. The template may be invalid.
          <div className="mt-4 flex justify-start">
            <Button onClick={handleBackToTemplates}>Back to Templates</Button>
          </div>
        </div>
      )
    }
  }
  const renderConfirmation = () => {
    if (!selectedTemplate) return null

    const confirmationText = createMode
      ? 'This template will be applied when you create the form.'
      : 'This will replace your current form design with the selected template. This action cannot be undone.'

    return (
      <div className="text-center">
        <div className="mx-auto mb-4 h-14 w-14 text-yellow-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            className="h-14 w-14"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h3 className="mb-5 text-lg font-medium text-gray-900">Are you sure you want to use this template?</h3>
        <p className="mb-5 text-gray-600">{confirmationText}</p>
        <div className="flex justify-center gap-4">
          <Button onClick={handleApplyTemplate} logo>
            Yes, Apply Template
          </Button>
          <Button onClick={handleBackToTemplates}>Cancel</Button>
        </div>
      </div>
    )
  }

  const buttonText = 'Use Template'

  return (
    <>
      <Button onClick={handleOpenModal} logo>
        {buttonText}
      </Button>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title="Form Templates" size="lg">
        {!previewMode && !confirmationMode && renderTemplateList()}
        {previewMode && renderPreview()}
        {confirmationMode && renderConfirmation()}
      </Modal>
    </>
  )
}
